import { color, LargeBtn } from "@funded-here-interface/common";
import { Text, Modal, Image } from "@mantine/core";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStyles } from "./TwoFAQRCode.styles";
import useGenerateTwoFASK from "@funded-here-interface/common/src/hooks/useGenerateTwoFASK";
import { useDisclosure } from "@mantine/hooks";
import useVerifyTwoFAToken from "@funded-here-interface/common/src/hooks/useVerifyTwoFAToken";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setEnvironment, setToken } from "../../features/auth/authSlice_Admin";
import { setInvestorToken } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUsers";
import phonedownload from "@funded-here-interface/common/src/assets/phonedownload.png";
import qrcodeframe from "@funded-here-interface/common/src/assets/qrcodeframe.png";
import complete2FA from "@funded-here-interface/common/src/assets/complete2FA.svg";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import OtpField from "../../components/OtpField/OtpField";
import {
  checkOnboardingLinkValid,
  sortRegtankRequestsByExpiry,
} from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";
import { RegtankOnboardingModal } from "@funded-here-interface/common/src/components/Regtank/RegtankOnboardingModel";
import { useBuildRegtankOnboardModel } from "@funded-here-interface/common/src/hooks/useBuildRegtankOnboardModel";
import { useNavigateAfterRegtank } from "@funded-here-interface/common/src/hooks/useNavigateAfterRegtank";
import { useRegtankOnboardingClick } from "@funded-here-interface/common/src/hooks/useRegtankOnboardingClick";
import { useInvestorAuthSync } from "../../hooks/useInvestorAuthSync";
import { getAdminNavigationPath } from "./utils/adminNavigation";
import {
  LAYOUT_STYLES,
  MODAL_STYLES,
  SVG_CONSTANTS,
} from "./TwoFAQRCode.constants";
import { RegtankRequest } from "../../Utils/navigation/types";

const TwoFAQRPageCommon: FC = () => {
  const { classes } = useStyles();
  const generateTwoFASecretKeyMutation = useGenerateTwoFASK();
  const verifyTwoFATokenMutation = useVerifyTwoFAToken();
  const [openedQRCode, modalAction] = useDisclosure(false);
  const [openedSuccess, modalActionSuccess] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);
  const getUserMutation = useGetUser();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const data = location.state;
  const email = data.email;
  const role = data.role;
  const persona = data.persona;
  const [img, setImage] = useState("");
  const [otp, setOtp] = useState("");
  const [jwtToken, setJwtToken] = useState("");
  const [openedRegtankOnboarding, modalActionRegtank] = useDisclosure(false);
  const onChange = (value: string) => {
    setOtp(value);
  };
  const [pendingOnBoardingAs, setPendingOnBoardingAs] = useState("");

  // Custom hooks
  const { syncInvestorAuthToRedux } = useInvestorAuthSync();

  // Regtank onboarding hooks
  const { handleNavigateNext } = useNavigateAfterRegtank();
  const modalContent = useBuildRegtankOnboardModel(pendingOnBoardingAs);
  const { isLoadingRegtank, setRegtankOnboardingUrl, handleRegtankClick } =
    useRegtankOnboardingClick(jwtToken);

  const handleNext = async () => {
    if (email) {
      generateTwoFASecretKeyMutation.mutate(
        { email },
        {
          onSuccess: (data) => {
            setIsLoading(true);
            setImage(data);
            setIsLoading(false);
          },
        }
      );
    }
  };

  const handleVerifyOtp = async () => {
    if (otp !== "") {
      verifyTwoFATokenMutation.mutate(
        { token: otp, email: email.toLowerCase(), persona: persona },
        {
          onSuccess: (data) => {
            if (data) {
              setJwtToken(data);
            }
            setIsLoading(false);
            modalAction.close();
            modalActionSuccess.open();
          },
          onError: (e) => {
            if (
              (e as Error).message.includes(
                "User is not verified for two factor authentication"
              )
            ) {
              const otpError: HTMLElement | null =
                document.getElementById("otpError");
              if (otpError) {
                otpError.innerHTML =
                  "<span style='color: red;'>" +
                  "The code you entered is incorrect. Please try again.</span>";
              }
            } else {
              toast.error((e as Error).message);
            }
          },
        }
      );
    }
  };

  const handleSuccess = () => {
    sessionStorage.setItem("Auth", jwtToken);
    const token = jwtToken;
    if (role === "Admin") {
      dispatch(setToken({ token }));
      getUserMutation.mutate(
        {
          token,
        },
        {
          onSuccess: (userData) => {
            const data = userData.user;
            const userRole = data?.orgRole?.[0]?.role?.name;
            const roleId = data?.currentOrgRoleId;

            sessionStorage.setItem("orgRoleId", roleId);

            dispatch(setEnvironment({ env: userData.environment }));

            // Use extracted navigation utility
            const navigationPath = getAdminNavigationPath(userRole);
            if (navigationPath) {
              navigate(navigationPath);
            } else {
              toast.error("Credentials are not valid.");
            }
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    }
    if (role === "Investor") {
      dispatch(setInvestorToken({ token }));
      getUserMutation.mutate(
        { token },
        {
          onSuccess: (data) => {
            const userDetail = data.user;
            // Set pendingOnBoardingAs for modal content
            setPendingOnBoardingAs(userDetail?.pendingOnBoardingAs || "");

            // Check and set existing Regtank URL if valid
            const requests = userDetail?.regtankRequest;
            let isValidOnboardingLink = false;

            if (requests && requests.length > 0) {
              const sortedRequests = sortRegtankRequestsByExpiry(
                requests
              ) as RegtankRequest[];
              const latestRequest = sortedRequests[0];

              isValidOnboardingLink = checkOnboardingLinkValid(
                latestRequest.onboardingStatus,
                latestRequest.expiredDate.toISOString()
              );

              if (isValidOnboardingLink) {
                setRegtankOnboardingUrl(latestRequest.verifyLink);
              }
            }

            sessionStorage.setItem("orgRoleId", userDetail.currentOrgRoleId);

            if (userDetail?.kycStatus === KYCStatus.REJECTED) {
              toast.error("Onboarding application has been rejected.");

              return;
            }

            if (userDetail?.spvDetails?.kycStatus === KYCStatus.REJECTED) {
              toast.error("SPV Onboarding application has been rejected.");

              return;
            }

            const hasCreditLimit = userDetail?.orgRole?.[0]?.creditLimit > 0;

            // Consolidate all Redux dispatches using custom hook
            syncInvestorAuthToRedux({
              agreedToMembershipAgreement:
                userDetail?.agreedToMembershipAgreement,
              orgName: userDetail?.orgName,
              agreedToSpvMembershipAgreement:
                userDetail?.agreedToSpvMembershipAgreement,
              hasCreditLimit,
              isAuthRep: userDetail?.isAuthRep,
              isAdminAuthRep: userDetail?.isAdminAuthRep,
              entityType: userDetail?.entityType,
              userId: userDetail?.id,
              userEmail: userDetail?.email,
              spvStatus: userDetail?.spvStatus,
              spvName: userDetail?.spvName,
              pendingOnBoardingAs: userDetail?.pendingOnBoardingAs,
              role: userDetail?.orgRole?.[0]?.role?.name,
              organization: userDetail?.organization,
              currentOrgRoleId: userDetail?.orgRoleId,
              firstName: userDetail?.firstName || null,
              lastName: userDetail?.lastName || null,
              spvDetails: userDetail?.spvDetails || null,
              regtankOnboardingFeatureFlag:
                userDetail?.regtankOnboardingFeatureFlag,
              regtankLivenessFeatureFlag:
                userDetail?.regtankLivenessFeatureFlag,
              regtankRequest: userDetail?.regtankRequest?.[0],
              regtankLiveness: userDetail?.regtankLiveness || null,
              regtankOnboarding: userDetail?.regtankOnboarding,
              declarationAISubmitted: userDetail?.declarationAISubmitted,
            });
            const result = handleNavigateNext(
              userDetail,
              isValidOnboardingLink,
              modalActionRegtank.open
            );
            if (result === "NO_ACTION_REQUIRED") {
              toast.warn("No action required at this time");
            }
          },
        }
      );
    }
  };

  useEffect(() => {
    if (img) {
      modalAction.open();
    }
  }, [img]);

  return (
    <div>
      <div className={classes.parentContainer}>
        <div className={classes.container}>
          <Text className={classes.headingContainer}>
            Two Factor Authentication (2FA)
          </Text>
          <hr className={classes.line} />
          <div style={LAYOUT_STYLES.twoColumnContainer}>
            <Text className={classes.secondheadingContainer}>
              Protect your account in just two steps!
            </Text>
          </div>
          <div style={LAYOUT_STYLES.twoColumnContainer}>
            <div style={LAYOUT_STYLES.leftColumn}>
              <Text className={classes.subHeading}>
                Step 1: Install an Authenticator App
              </Text>
            </div>
            <div style={LAYOUT_STYLES.rightColumn}>
              <Text className={classes.subHeading}>
                Step 2: Scan and Verify
              </Text>
            </div>
          </div>
          <div style={LAYOUT_STYLES.twoColumnContainer}>
            <div style={LAYOUT_STYLES.leftColumn}>
              <Text className={classes.instructionHeading}>
                Download and install an authenticator app on your phone from
                Google Play or the App Store.
              </Text>
            </div>
            <div style={LAYOUT_STYLES.rightColumn}>
              <Text className={classes.instructionHeading}>
                Scan the QR code using your authenticator app and enter the
                6-digit code generated by the app to verify your 2FA setup.
              </Text>
            </div>
          </div>
          <div style={LAYOUT_STYLES.twoColumnContainer}>
            <div style={LAYOUT_STYLES.leftColumn}>
              <div className={classes.photoDisplay}>
                <Image src={phonedownload} />
              </div>
            </div>
            <div style={LAYOUT_STYLES.rightColumn}>
              <div className={classes.photoDisplayQR}>
                <Image src={qrcodeframe} />
              </div>
              <div style={MODAL_STYLES.qrCodeSeparator}>
                <svg
                  width={SVG_CONSTANTS.dashedLine.width}
                  height={SVG_CONSTANTS.dashedLine.height}
                >
                  <line
                    x1="0"
                    y1="10"
                    x2={SVG_CONSTANTS.dashedLine.width}
                    y2="10"
                    stroke={SVG_CONSTANTS.dashedLine.stroke}
                    strokeWidth={SVG_CONSTANTS.dashedLine.strokeWidth}
                    strokeDasharray={SVG_CONSTANTS.dashedLine.dashArray}
                  />
                </svg>
              </div>
            </div>
          </div>
          <div style={LAYOUT_STYLES.twoColumnContainer}>
            <div style={LAYOUT_STYLES.leftColumn}>
              <Text className={classes.instructionHeadingItalic}>
                Recommended apps:
              </Text>
              <ul className={classes.ulList}>
                <li>Google Authenticator</li>
                <li>Microsoft Authenticator</li>
              </ul>
            </div>
          </div>
          <div style={LAYOUT_STYLES.centeredContent}>
            <LargeBtn
              onClick={handleNext}
              backgroundColor={color.FHGREEN}
              textColor="#FFFFFF"
              width="auto"
              padding="15px"
            >
              Continue to Scan QR Code
            </LargeBtn>
          </div>
        </div>
      </div>
      <Modal
        classNames={{
          header: classes.modalHeader,
          title: classes.modalTitle,
        }}
        title="Scan and Verify"
        opened={openedQRCode}
        onClose={modalAction.close}
        size="sm"
        overlayProps={{
          blur: 3,
        }}
      >
        <div style={LAYOUT_STYLES.centeredContent}>
          <Text className={classes.instructionHeading}>
            Scan the QR Code below using your authenticator app.
          </Text>
        </div>
        {img ? (
          <div>
            <div className={classes.photoDisplay}>
              <Image src={img} />
            </div>
            <OtpField
              length={6}
              onChange={onChange}
              onEnter={handleVerifyOtp}
            />
            <div style={MODAL_STYLES.otpErrorContainer}>
              <span id="otpError"></span>
            </div>
            <div style={LAYOUT_STYLES.centeredContent}>
              <LargeBtn
                onClick={handleVerifyOtp}
                backgroundColor={color.FHGREEN}
                textColor="#FFFFFF"
                width="5%"
                disabled={otp !== "" ? false : true}
              >
                Verify
              </LargeBtn>
            </div>
          </div>
        ) : (
          <p>Loading QR code...</p>
        )}
      </Modal>
      <Modal
        classNames={{
          header: classes.modalHeader,
          title: classes.modalTitle,
        }}
        title="Two-Factor Authentication Setup Complete"
        size="sm"
        overlayProps={{
          blur: 3,
        }}
        opened={openedSuccess}
        withCloseButton={false}
        onClose={modalActionSuccess.close}
      >
        <div className={classes.photoDisplay2FA}>
          <Image src={complete2FA} />
        </div>
        <div style={LAYOUT_STYLES.centeredContentWithMargin}>
          <Text className={classes.headingContainer}>2FA Enabled.</Text>
        </div>
        <div style={LAYOUT_STYLES.centeredBlock}>
          <text className={classes.instructionHeadingNoMargin}>
            Your account is now more secure.
          </text>
          <br></br>
          <text className={classes.instructionHeadingNoMargin}>
            You will be asked for a code each time you log in.
          </text>
        </div>
        <div style={LAYOUT_STYLES.centeredButtonContainer}>
          <LargeBtn
            onClick={handleSuccess}
            backgroundColor={color.FHGREEN}
            textColor="#FFFFFF"
            width="10"
          >
            Done
          </LargeBtn>
        </div>
      </Modal>
      <RegtankOnboardingModal
        opened={openedRegtankOnboarding}
        onClose={modalActionRegtank.close}
        title={modalContent.regtankModalTitle}
        firstLine={modalContent.regtankFirstLine}
        secondLine={modalContent.regtankSecLine}
        onButtonClick={handleRegtankClick}
        isLoadingOnboarding={isLoadingRegtank}
      />
    </div>
  );
};

export default TwoFAQRPageCommon;
